import {
    keyBy as _keyBy,
    isEmpty as _isEmpty,
} from '@lodash';
import IStructDto from '@core/mappers/struct/dtos/IStructDto';
import IResponse from '@core/utils/types/IResponse';
import Struct from '@core/models/struct/Struct';
import MalformedDataError from '@errors/network/MalformedDataError';
import Logger from '@loggers/Logger';
import SportMapper from '@core/mappers/struct/SportMapper';
import MarketMapper from '@core/mappers/struct/MarketMapper';
import OddTypeMapper from '@core/mappers/struct/OddTypeMapper';
import CategoryMapper from '@core/mappers/struct/CategoryMapper';
import TournamentMapper from '@core/mappers/struct/TournamentMapper';
import SuperOfferMapper from '@core/mappers/offer/SuperOfferMapper';
import EventMapper from '@core/mappers/offer/EventMapper';
import RestService from './_private/RestService';
import ServerConfig from '@core/models/country/ServerConfig';
import { instanceGuard } from '@core/utils/services';
import SingletonError from '../common/errors/SingletonError';
import Singleton from '../common/Singleton';
import extractLocalizedString from '@core/utils/mappers/helpers';
import MarketGroupMapper from '@core/mappers/struct/MarketGroupMapper';
const marketGroupMapper = new MarketGroupMapper();
class Mappers {
    sportMapper?: SportMapper = new SportMapper();
    tournamentMapper?: TournamentMapper = new TournamentMapper();
    categoryMapper?: CategoryMapper = new CategoryMapper();
    marketMapper?: MarketMapper = new MarketMapper();
    oddTypeMapper?: OddTypeMapper = new OddTypeMapper();
    superOfferMapper?: SuperOfferMapper = new SuperOfferMapper();
    eventMapper?: EventMapper = new EventMapper();
    marketGroupMapper?: MarketGroupMapper = new MarketGroupMapper();
}

export interface ISuperOfferConfig {
    type: string;
    name: string;
    id: string;
    footer: string;
}

export interface Dependencies {
    offerHostProvider: { getCurrentOfferHost: () => string };
}

class StructService {
    private mappers: Mappers;
    private restService: RestService;
    private static instance?: StructService;

    private constructor(config: ServerConfig, { offerHostProvider }: Dependencies) {
        if (StructService.instance) {
            throw new SingletonError(this.constructor.name);
        }
        this.mappers = new Mappers();
        console.log('SSBT::StructService hostServer >>', offerHostProvider.getCurrentOfferHost());
        this.restService = new RestService(offerHostProvider.getCurrentOfferHost());
    }

    public static getInstance(): StructService {
        return instanceGuard(StructService.instance)!;
    }

    public static createInstance(config: ServerConfig, dependencies: Dependencies) {
        if (!StructService.instance) {
            StructService.instance = new StructService(config, dependencies);
        }
        return StructService.instance;
    }

    private raiseAndLogMalformedDataError(msg: string) {
        const error = new MalformedDataError(msg);
        Logger.error(error);
        throw error;
    }

    public setRestService(restService: RestService) {
        this.restService = restService;
    }

    /**
     * @throws {RequestError}
     * @throws {MalformedDataError}
     * @throws {Error}
     */
    public async getStruct(): Promise<Struct> {
        const json: IResponse<IStructDto> = await this.restService.getStruct();
        const hasEmptyValues: boolean = [
                json.data.tournaments,
                json.data.sports,
                json.data.betGroups,
                json.data.oddTypes,
                json.data.categories,
                json.data.marketGroups,
            ].some((value) => _isEmpty(value));
        if ((json as any).error || hasEmptyValues) {
            this.raiseAndLogMalformedDataError('Struct has empty structures');
        }
        const superOffer = json.data.superOffer;
        const superOfferConfig = {} as Record<string, ISuperOfferConfig>;

        Object.entries(superOffer).forEach(([key, offer]) => {
            // check with filke why the key is superCota and typeValue superKvota
            // PS. odd.tags are also superKvota
            let _key = key;
            // only hardcoded thing concerning super offer
            if (key === 'superCota') {
                _key = 'superKvota';
            }
            superOfferConfig[_key] = {
                id: offer._id,
                type: offer.typeValue,
                name: extractLocalizedString(offer.name),
                footer: offer.footer ? extractLocalizedString(offer.footer) : '',
            };
        });

        // @ts-ignore
        const struct = {
            superOffer: superOfferConfig,
            tournaments: _keyBy(this.mappers.tournamentMapper!.map(json.data.tournaments, true), 'id'),
            sports: _keyBy(this.mappers.sportMapper!.map(json.data.sports, true), 'id'),
            markets: _keyBy(this.mappers.marketMapper!.map(json.data.betGroups, true), 'id'),
            oddTypes: _keyBy(this.mappers.oddTypeMapper!.map(json.data.oddTypes, true), 'id'),
            categories: _keyBy(this.mappers.categoryMapper!.map(json.data.categories, true), 'id'),
            marketGroupInfos: _keyBy(this.mappers.marketGroupMapper!.map(json.data.marketGroups, true), 'id'),
        } as Struct;

        return struct;
    }

    public static clearInstance() {
        if (process.env.NODE_ENV !== 'test') {
            throw new Error('For use in tests only');
        }
        delete StructService.instance;
    }
}

export interface IStructService extends StructService {}

export default StructService as Singleton<StructService>;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--12!./src/core/services/offer/StructService.ts